import {
  ConnectionQualityLevels,
  ConnectionStates,
  DeviceErrors,
  StreamStates,
} from "@minervaproject/avsubsystem-core";

import { NavigationTimingMetricsType } from "./monitoring";
import { PreclassTestOutcomeType, PreclassTestResultType } from "./preclass-check";
import { WebsocketStatusType } from "./pubsub";

export const PUBSUB_VIRTUAL_CLASS_STATE_MESSAGE_TYPE = "pubsub-virtual-class-state";
export const REDIS_PUBSUB_VIRTUAL_CLASS_STATE_CHANNEL = "redis-pubsub-virtual-class-state";

export const PINNED_CLASS_USER_EXPIRATION_MS = 20 * 60 * 1000;
export const MAX_PINNED_CLASS_USER_SLOTS = 3;

export type PinnedClassUserEntry = {
  id: string;
  pinned_at: number;
};

// Ping RTT stats for one socket (pubsub or sharedb).
export interface PingRttType {
  isHigh: boolean;
  isLow: boolean;
  median: number | undefined;
  min: number;
  max: number;
}

// Combined socket state for display; stored as separate flat fields.
export interface SocketUserState {
  pingRtt?: PingRttType;
  websocketStatus?: WebsocketStatusType;
  websocketStatusReason?: string;
}

export interface PubsubVirtualUserState {
  // Set on join: which websocket (tab) is this user's main AV connection.
  activeWebsocketId?: string | null;
  avRtt?: string;
  cameraError?: DeviceErrors;
  connectionQuality?: ConnectionQualityLevels;
  connectionState?: ConnectionStates;
  currentCamera?: string;
  currentMic?: string;
  currentSpeaker?: string;
  isAudioEnabled?: boolean;
  isPresent?: boolean;
  isSpeaking?: boolean;
  lastSpokeAt?: number | null;
  isVideoEnabled?: boolean;
  micError?: DeviceErrors;
  navigationTimingMetrics?: NavigationTimingMetricsType;
  preclassCheck?: {
    outcome: PreclassTestOutcomeType;
    tests: PreclassTestResultType[];
  };
  publishedStreamState?: StreamStates;
  userAgent?: string;

  // Socket state for pubsub.
  pubsubPingRtt?: PingRttType;
  pubsubWebsocketStatus?: WebsocketStatusType;
  pubsubWebsocketStatusReason?: string;

  // Socket state for sharedb.
  shareDBPingRtt?: PingRttType;
  shareDBWebsocketStatus?: WebsocketStatusType;
  shareDBWebsocketStatusReason?: string;
}

export type PubsubVirtualUsersState = Record<string, PubsubVirtualUserState>;

export interface PubsubVirtualClassState {
  users: PubsubVirtualUsersState;
  last_updated_at: number | null;
  av_active_class_user_ids?: string[];
  leader_class_user_id?: string | null;
  pinned_class_user_ids?: string[];
}

export enum AvTabState {
  NotMain = "not-main",
  Main = "main",
  Retrying = "retrying",
}

export interface ClientLocalPubsubVirtualClassState extends PubsubVirtualClassState {
  av_active_class_user_ids: string[];
  leader_class_user_id: string | null;
  pinned_class_user_ids: string[];
  av_tab_state?: AvTabState;
  observing_session_id: string | null;
  observer_listened_breakout_group_id: string | null;
  observer_breakout_group_id: string | null;
  max_participant_bar_slots: number;
  // The visible class user ids in the participant bar. These are the users that fit in max_participant_bar_slots.
  participant_bar_visible_class_user_ids: string[];
}
type PubsubVirtualUserAlreadyJoinedMessageType = {
  action: "already-joined";
  virtual_user_id: string;
};
type PubsubVirtualUserMuteMessageType = {
  action: "mute";
};
type PubsubVirtualUserSyncMessageType = {
  action: "sync";
  data: {
    users: PubsubVirtualUsersState;
    av_active_class_user_ids?: string[];
    leader_class_user_id?: string | null;
    pinned_class_user_ids?: string[];
  };
};
type PubsubVirtualUserAvActiveUpdatedMessageType = {
  action: "av-active-updated";
  av_active_class_user_ids: string[];
};
type PubsubVirtualUserPinnedUpdatedMessageType = {
  action: "pinned-updated";
  pinned_class_user_ids: string[];
};
type PubsubVirtualUserLeaderUpdatedMessageType = {
  action: "leader-updated";
  leader_class_user_id: string;
};
type PubsubVirtualUserUpdateMessageFromServerType = {
  action: "update";
  data: PubsubVirtualUserState;
  virtual_user_id: string;
};

export type PubsubVirtualClassStateMessageFromServerType = {
  type: typeof PUBSUB_VIRTUAL_CLASS_STATE_MESSAGE_TYPE;
  last_updated_at: number;
} & (
  | PubsubVirtualUserSyncMessageType
  | PubsubVirtualUserAlreadyJoinedMessageType
  | PubsubVirtualUserMuteMessageType
  | PubsubVirtualUserUpdateMessageFromServerType
  | PubsubVirtualUserAvActiveUpdatedMessageType
  | PubsubVirtualUserPinnedUpdatedMessageType
  | PubsubVirtualUserLeaderUpdatedMessageType
);

export type PubsubVirtualUserJoinMessageType = {
  action: "join";
  force: boolean;
};
export type PubsubVirtualUserLeaveMessageType = {
  action: "leave";
};

export type PubsubVirtualUserMuteAllMessageType = {
  action: "mute-all";
};

export type PubsubVirtualUserMuteUsersMessageType = {
  action: "mute-users";
  user_ids: string[];
};

export type PubsubVirtualUserUpdateMessageFromClientType = {
  action: "update";
  data: PubsubVirtualUserState;
};

export type PubsubVirtualUserUpdateLeaderMessageType = {
  action: "update-leader";
};

export type PubsubVirtualUserUpdateAvActiveListMessageType = {
  action: "update-av-active-list";
  av_active_class_user_ids: string[];
};

export type PubsubVirtualUserPinClassUsersMessageType = {
  action: "pin-class-users";
  class_user_ids: string[];
};

export type PubsubVirtualUserClearPinnedClassUsersMessageType = {
  action: "clear-pinned-class-users";
};

export type BarePubsubVirtualClassStateMessageFromClientType =
  | PubsubVirtualUserJoinMessageType
  | PubsubVirtualUserLeaveMessageType
  | PubsubVirtualUserMuteAllMessageType
  | PubsubVirtualUserMuteUsersMessageType
  | PubsubVirtualUserUpdateMessageFromClientType
  | PubsubVirtualUserUpdateLeaderMessageType
  | PubsubVirtualUserUpdateAvActiveListMessageType
  | PubsubVirtualUserPinClassUsersMessageType
  | PubsubVirtualUserClearPinnedClassUsersMessageType;

export type PubsubVirtualClassStateMessageFromClientType = {
  type: typeof PUBSUB_VIRTUAL_CLASS_STATE_MESSAGE_TYPE;
  virtual_user_id: string;
  class_session_id?: string | null;
} & BarePubsubVirtualClassStateMessageFromClientType;

export type RedisPubsubVirtualClassStateUpdateAction = {
  action: "update";
  virtual_user_id: string;
  data: PubsubVirtualUserState;
};

export type RedisPubsubVirtualClassStateMuteAllAction = {
  action: "mute-all";
  user_id: string;
};

export type RedisPubsubVirtualClassStateMuteUsersAction = {
  action: "mute-users";
  user_ids: string[];
};

export type RedisPubsubVirtualClassStateAvActiveUpdatedAction = {
  action: "av-active-updated";
  av_active_class_user_ids: string[];
};

export type RedisPubsubVirtualClassStateLeaderUpdatedAction = {
  action: "leader-updated";
  leader_class_user_id: string;
};

export type RedisPubsubVirtualClassStatePinnedUpdatedAction = {
  action: "pinned-updated";
  pinned_class_user_ids: string[];
};

export type RedisPubsubVirtualClassStateMessageType = {
  type: typeof REDIS_PUBSUB_VIRTUAL_CLASS_STATE_CHANNEL;
  section_id: string;
  last_updated_at: number;
} & (
  | RedisPubsubVirtualClassStateUpdateAction
  | RedisPubsubVirtualClassStateMuteAllAction
  | RedisPubsubVirtualClassStateMuteUsersAction
  | RedisPubsubVirtualClassStateAvActiveUpdatedAction
  | RedisPubsubVirtualClassStatePinnedUpdatedAction
  | RedisPubsubVirtualClassStateLeaderUpdatedAction
);
